import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ArrowLeft, Mail, Shield, Loader2, TrendingUp, User, Lock, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const InvestorLoginPage = () => {
  const navigate = useNavigate();
  const [loginMethod, setLoginMethod] = useState("username"); // "username" or "otp"
  
  // Username/Password login state
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  // OTP login state
  const [step, setStep] = useState(1);
  const [pan, setPan] = useState("");
  const [otp, setOtp] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [expiryMinutes, setExpiryMinutes] = useState(10);
  
  // Change password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [sessionToken, setSessionToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  const [loading, setLoading] = useState(false);

  // Username/Password Login
  const handleUsernameLogin = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error("Please enter username and password");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/investor-auth/login`, {
        username: username.toLowerCase(),
        password: password
      });
      
      const { token, investor, must_change_password } = response.data;
      
      if (must_change_password) {
        setSessionToken(token);
        setShowChangePassword(true);
        toast.info("Please change your password to continue");
      } else {
        // Store token and investor info
        localStorage.setItem("investor_token", token);
        localStorage.setItem("investor_data", JSON.stringify(investor));
        toast.success("Login successful!");
        navigate("/investor/dashboard");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Change Password
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/investor-auth/change-password`, {
        token: sessionToken,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      
      toast.success("Password changed successfully!");
      
      // Now complete the login
      localStorage.setItem("investor_token", sessionToken);
      
      // Get investor data
      const validateRes = await axios.get(`${API}/investor-auth/validate-session/${sessionToken}`);
      localStorage.setItem("investor_data", JSON.stringify(validateRes.data.investor));
      
      navigate("/investor/dashboard");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to change password";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // OTP Login - Request OTP
  const handleRequestOTP = async (e) => {
    e.preventDefault();
    
    if (!pan || pan.length !== 10) {
      toast.error("Please enter a valid 10-character PAN");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/investor-auth/request-otp`, {
        pan: pan.toUpperCase()
      });
      
      setMaskedEmail(response.data.message.replace("OTP sent to ", ""));
      setExpiryMinutes(response.data.expiry_minutes || 10);
      setStep(2);
      toast.success("OTP sent to your registered email");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to send OTP";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // OTP Login - Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (!otp || otp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/investor-auth/verify-otp`, {
        pan: pan.toUpperCase(),
        otp: otp
      });
      
      localStorage.setItem("investor_token", response.data.token);
      localStorage.setItem("investor_data", JSON.stringify(response.data.investor));
      
      toast.success("Login successful!");
      navigate("/investor/dashboard");
    } catch (error) {
      const message = error.response?.data?.detail || "Invalid OTP";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/investor-auth/request-otp`, {
        pan: pan.toUpperCase()
      });
      toast.success("New OTP sent to your email");
      setExpiryMinutes(response.data.expiry_minutes || 10);
    } catch (error) {
      toast.error("Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  // Change Password Form
  if (showChangePassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
        </div>

        <div className="w-full max-w-md relative z-10">
          <Card className="bg-slate-900/80 border-slate-800 backdrop-blur-xl shadow-2xl">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-slate-900" />
              </div>
              <CardTitle className="text-2xl font-bold text-white">Change Password</CardTitle>
              <CardDescription className="text-slate-400">
                Please set a new password to continue
              </CardDescription>
            </CardHeader>

            <CardContent className="pt-4">
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-slate-300">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="bg-slate-800/50 border-slate-700 text-white pr-10"
                      placeholder="Enter new password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-slate-300">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="bg-slate-800/50 border-slate-700 text-white"
                    placeholder="Confirm new password"
                    required
                  />
                </div>

                <div className="p-3 bg-slate-800/50 rounded-lg text-xs text-slate-400">
                  <p className="font-medium mb-1">Password requirements:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>At least 8 characters</li>
                    <li>At least one uppercase letter</li>
                    <li>At least one lowercase letter</li>
                    <li>At least one number</li>
                  </ul>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900 font-semibold h-11"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Changing Password...
                    </>
                  ) : (
                    "Change Password & Continue"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Back to home */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <Card className="bg-slate-900/80 border-slate-800 backdrop-blur-xl shadow-2xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-500/20">
              <TrendingUp className="w-8 h-8 text-slate-900" />
            </div>
            <CardTitle className="text-2xl font-bold text-white">Investor Portal</CardTitle>
            <CardDescription className="text-slate-400">
              Access your mutual fund portfolio
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-4">
            <Tabs value={loginMethod} onValueChange={setLoginMethod} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6 bg-slate-800">
                <TabsTrigger value="username" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900">
                  <User className="w-4 h-4 mr-2" />
                  Username
                </TabsTrigger>
                <TabsTrigger value="otp" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900">
                  <Mail className="w-4 h-4 mr-2" />
                  Email OTP
                </TabsTrigger>
              </TabsList>

              {/* Username/Password Login */}
              <TabsContent value="username">
                <form onSubmit={handleUsernameLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="username" className="text-slate-300">Username</Label>
                    <Input
                      id="username"
                      type="text"
                      placeholder="Enter your username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                      data-testid="username-input"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-slate-300">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 pr-10"
                        data-testid="password-input"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    data-testid="login-btn"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900 font-semibold h-11"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Logging in...
                      </>
                    ) : (
                      <>
                        <User className="w-4 h-4 mr-2" />
                        Login
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>

              {/* OTP Login */}
              <TabsContent value="otp">
                {step === 1 ? (
                  <form onSubmit={handleRequestOTP} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="pan" className="text-slate-300">PAN Number</Label>
                      <Input
                        id="pan"
                        data-testid="pan-input"
                        type="text"
                        placeholder="Enter your PAN (e.g., ABCDE1234F)"
                        value={pan}
                        onChange={(e) => setPan(e.target.value.toUpperCase().slice(0, 10))}
                        className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 uppercase tracking-wider"
                        maxLength={10}
                        required
                      />
                    </div>

                    <Button
                      type="submit"
                      data-testid="request-otp-btn"
                      disabled={loading || pan.length !== 10}
                      className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900 font-semibold h-11"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending OTP...
                        </>
                      ) : (
                        <>
                          <Mail className="w-4 h-4 mr-2" />
                          Send OTP to Email
                        </>
                      )}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleVerifyOTP} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="otp" className="text-slate-300">Enter OTP</Label>
                      <p className="text-xs text-slate-500">Sent to {maskedEmail}</p>
                      <Input
                        id="otp"
                        data-testid="otp-input"
                        type="text"
                        placeholder="Enter 6-digit OTP"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="bg-slate-800/50 border-slate-700 text-white text-center text-2xl tracking-[0.5em] font-mono"
                        maxLength={6}
                        required
                      />
                      <p className="text-xs text-slate-500 text-center">
                        OTP valid for {expiryMinutes} minutes
                      </p>
                    </div>

                    <Button
                      type="submit"
                      data-testid="verify-otp-btn"
                      disabled={loading || otp.length !== 6}
                      className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900 font-semibold h-11"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <Shield className="w-4 h-4 mr-2" />
                          Verify & Login
                        </>
                      )}
                    </Button>

                    <div className="flex items-center justify-between pt-2">
                      <button
                        type="button"
                        onClick={() => { setStep(1); setOtp(""); }}
                        className="text-sm text-slate-400 hover:text-amber-400 transition-colors"
                      >
                        Change PAN
                      </button>
                      <button
                        type="button"
                        data-testid="resend-otp-btn"
                        onClick={handleResendOTP}
                        disabled={loading}
                        className="text-sm text-amber-400 hover:text-amber-300 transition-colors disabled:opacity-50"
                      >
                        Resend OTP
                      </button>
                    </div>
                  </form>
                )}
              </TabsContent>
            </Tabs>

            {/* Help text */}
            <div className="mt-6 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <h4 className="text-sm font-medium text-slate-300 mb-2">Need help?</h4>
              <p className="text-xs text-slate-400">
                Contact your distributor if you don't have login credentials or need to reset your password.
              </p>
            </div>

            {/* Distributor login link */}
            <div className="mt-4 text-center">
              <p className="text-sm text-slate-500">
                Are you a distributor?{" "}
                <Link to="/login" className="text-amber-400 hover:text-amber-300 transition-colors">
                  Login here
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InvestorLoginPage;
